import { atom } from "jotai"
import { useMemo } from "react"
import { openProject, OpenProjectOptions } from "@/store/project/open-project"
import { closeProject } from "@/store/project/project"
import { store } from "@/store/store"
import { projectAtom } from "@/store/project/projects-state"
import { browserCapabilitiesAtom } from "@/store/app/browser-capabilities"
import { replaceDialog } from "@/store/dialogs-state"
import { SwitchToChromeDialog } from "@/components/Dialogs/SwitchToChromeDialog"
import { routes } from "@/utils/routes"

type SessionStatus = "idle" | "opening" | "open" | "closing"

type SessionState = {
	status: SessionStatus
	projectId: string | null
	activeOp: Promise<void> | null
}

type CloseOptions = {
	saveChanges?: boolean
	reason?: string
	skipNavigateHome?: boolean
	skipResetDialogs?: boolean
	forceClose?: boolean
}

export const projectSessionAtom = atom<SessionState>({
	status: "idle",
	projectId: null,
	activeOp: null,
})

export function setState(next: Partial<SessionState>) {
	const current = store.get(projectSessionAtom)
	store.set(projectSessionAtom, {
		status: next.status !== undefined ? next.status : current.status,
		projectId: next.projectId !== undefined ? next.projectId : current.projectId,
		activeOp: next.activeOp !== undefined ? next.activeOp : current.activeOp,
	})
}

async function runExclusive(fn: () => Promise<void>) {
	// Chain operations to prevent overlapping open/close work.
	const current = store.get(projectSessionAtom)
	const op = (current.activeOp ?? Promise.resolve()).then(fn).catch((error) => {
		// Reset state on failure so a subsequent request can recover.
		setState({ status: "idle", projectId: null, activeOp: null })
		throw error
	})

	setState({ activeOp: op })

	try {
		await op
	} finally {
		if (store.get(projectSessionAtom).activeOp === op) {
			setState({ activeOp: null })
		}
	}
}

async function ensureClosed(options?: CloseOptions) {
	const current = store.get(projectSessionAtom)
	// Has to disable this otherwise calling closeSession when idle will hang.
	if (current.status === "idle") {
		return
	}

	if (current.status === "closing") {
		return current.activeOp
	}

	setState({ status: "closing" })
	await closeProject(options?.saveChanges ?? true, {
		skipNavigateHome: options?.skipNavigateHome,
		skipResetDialogs: options?.skipResetDialogs,
	})
	setState({ status: "idle", projectId: null })
}

/**
 * Mark the session as opening for a specific project.
 * Call this before directly calling setUpProject to prevent the router from triggering a duplicate open.
 */
export function markSessionOpening(projectId: string) {
	setState({ status: "opening", projectId })
}

/**
 * Finalize session state.
 * If this session is still opening the same project and projectAtom points to it,
 * mark the session as open; otherwise clear stale opening state back to idle.
 */
export function finalizeSession(projectId?: string) {
	const current = store.get(projectSessionAtom)
	if (current.status !== "opening") {
		return
	}

	const currentProject = store.get(projectAtom)
	if (projectId && currentProject?.id === projectId) {
		setState({ status: "open", projectId })
	} else {
		setState({ status: "idle", projectId: null, activeOp: null })
	}
}

export async function openSession(options: OpenProjectOptions) {
	const targetId = options.project.id

	// In sample-only browsers, block opening non-sample projects and show the
	// "Switch to Chrome or download our app" dialog instead of attempting to
	// open the project (no filesystem work, no loading dialog, etc.).
	const capabilities = store.get(browserCapabilitiesAtom)
	if (capabilities.supportsSampleOnly && !options.project.isSampleProject) {
		// When blocking a non-sample project from a project route in a
		// sample-only browser, show the dialog and, once dismissed, send the
		// user back to the root/home route.
		replaceDialog(SwitchToChromeDialog, {
			onDismiss: () => {
				void import("@/router").then(({ router }) => {
					void routes.root.navigate(router)
				})
			},
		})
		return
	}

	const current = store.get(projectSessionAtom)
	if (
		current.projectId === targetId &&
		(current.status === "open" || current.status === "opening")
	) {
		// Already opening/opening this project; let the in-flight op finish.
		return current.activeOp
	}

	return runExclusive(async () => {
		const current = store.get(projectSessionAtom)
		// If another project is open, close it before opening the next one.
		if (current.status === "open" && current.projectId && current.projectId !== targetId) {
			await ensureClosed({
				saveChanges: true,
				reason: "switch_project",
				skipNavigateHome: true,
			})
		}

		setState({ status: "opening", projectId: targetId })
		await openProject(options)
	})
}

export async function closeSession(options?: CloseOptions) {
	if (store.get(projectSessionAtom).status === "idle") {
		return
	}

	if (options?.forceClose === true) {
		// Force close immediately resets state without running exclusive logic.
		await ensureClosed(options)
		setState({ activeOp: null, projectId: null, status: "idle" })
	} else {
		return runExclusive(async () => {
			await ensureClosed(options)
		})
	}
}

export function useProjectSession() {
	return useMemo(
		() => ({
			open: openSession,
			close: closeSession,
		}),
		[],
	)
}

export async function navigateToProjectRoute(projectId: string) {
	try {
		const { router } = await import("@/router")
		const targetPath = routes.project.build({ projectId })

		if (router.state.location.pathname !== targetPath) {
			await routes.project.navigate(router, { projectId })
		}
	} catch (error) {
		console.error("Failed to navigate to project route", error)
	}
}
